import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy,
  Star,
  Clock,
  Heart,
  XCircle,
  ArrowRight,
  Award,
  Target,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import Button from '../ui/Button';

interface ChallengeExperienceProps {
  isOpen: boolean;
  onClose: () => void;
  challenge: any;
}

const ChallengeExperience: React.FC<ChallengeExperienceProps> = ({
  isOpen,
  onClose,
  challenge
}) => {
  const [lives, setLives] = useState(3);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(challenge.duration * 60); // Convert minutes to seconds
  const [currentTask, setCurrentTask] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [gameOver, setGameOver] = useState(false);
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);

  // Simulated tasks for the challenge
  const tasks = [
    {
      id: 1,
      type: 'code',
      description: 'Debug the following code snippet',
      content: `function calculateTotal(items) {
  let total = 0;
  items.forEach(item => {
    total += item.price;
  });
  return total
}`,
      options: [
        { id: 'a', text: 'Missing semicolon after return statement', correct: true },
        { id: 'b', text: 'forEach should be a for loop', correct: false },
        { id: 'c', text: 'total should be const', correct: false }
      ]
    },
    {
      id: 2,
      type: 'scenario',
      description: 'A critical production bug is reported. What\'s your first action?',
      options: [
        { id: 'a', text: 'Immediately roll back to the last stable version', correct: false },
        { id: 'b', text: 'Assess the impact and severity first', correct: true },
        { id: 'c', text: 'Start debugging without any assessment', correct: false }
      ]
    },
    {
      id: 3,
      type: 'architecture',
      description: 'Choose the best database for a real-time chat application',
      options: [
        { id: 'a', text: 'PostgreSQL', correct: false },
        { id: 'b', text: 'MongoDB', correct: false },
        { id: 'c', text: 'Firebase Realtime Database', correct: true }
      ]
    }
  ];

  useEffect(() => {
    if (isOpen && !gameOver) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setGameOver(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen, gameOver]);

  const handleAnswer = (optionId: string) => {
    const currentTaskData = tasks[currentTask];
    const selectedOption = currentTaskData.options.find(opt => opt.id === optionId);
    
    if (selectedOption?.correct) {
      setScore(prev => prev + 100);
      setFeedbackType('success');
      if (score + 100 >= 250 && !earnedBadges.includes('problem-solver')) {
        setEarnedBadges(prev => [...prev, 'problem-solver']);
      }
    } else {
      setLives(prev => prev - 1);
      setFeedbackType('error');
      if (lives <= 1) {
        setGameOver(true);
      }
    }

    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
      if (currentTask < tasks.length - 1) {
        setCurrentTask(prev => prev + 1);
      } else {
        setGameOver(true);
      }
    }, 1500);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full p-6 md:p-8"
            >
              {/* Game Header */}
              <div className="flex justify-between items-center mb-8">
                <div className="flex items-center space-x-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Heart
                      key={i}
                      className={`h-6 w-6 ${i < lives ? 'text-red-500' : 'text-gray-300'}`}
                      fill={i < lives ? 'currentColor' : 'none'}
                    />
                  ))}
                </div>
                <div className="text-2xl font-bold text-primary-600">
                  {score} pts
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="h-5 w-5 mr-2" />
                  {formatTime(timeLeft)}
                </div>
              </div>

              {!gameOver ? (
                <>
                  {/* Current Task */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">
                        Task {currentTask + 1} of {tasks.length}
                      </h3>
                      <div className="text-sm text-gray-500">
                        {tasks[currentTask].type}
                      </div>
                    </div>
                    
                    <p className="text-lg text-gray-700 mb-6">
                      {tasks[currentTask].description}
                    </p>

                    {tasks[currentTask].type === 'code' && (
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg mb-6 overflow-x-auto">
                        <code>{tasks[currentTask].content}</code>
                      </pre>
                    )}

                    <div className="space-y-4">
                      {tasks[currentTask].options.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleAnswer(option.id)}
                          className="w-full text-left p-4 rounded-xl border-2 hover:border-primary-500 hover:bg-primary-50 transition-all duration-200"
                        >
                          {option.text}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Feedback Overlay */}
                  <AnimatePresence>
                    {showFeedback && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-2xl"
                      >
                        <div className={`p-8 rounded-full ${
                          feedbackType === 'success' ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {feedbackType === 'success' ? (
                            <CheckCircle className="h-16 w-16 text-white" />
                          ) : (
                            <XCircle className="h-16 w-16 text-white" />
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                // Game Over Screen
                <div className="text-center">
                  <div className="mb-8">
                    {score >= 250 ? (
                      <div className="inline-block p-6 bg-primary-100 rounded-full mb-4">
                        <Trophy className="h-16 w-16 text-primary-600" />
                      </div>
                    ) : (
                      <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
                        <Target className="h-16 w-16 text-gray-600" />
                      </div>
                    )}
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Challenge Complete!
                    </h2>
                    <p className="text-gray-600">
                      {score >= 250 
                        ? 'Congratulations! You\'ve mastered this challenge!' 
                        : 'Keep practicing to improve your skills!'}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 mb-8">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary-600 mb-1">
                          {score}
                        </div>
                        <div className="text-sm text-gray-500">Points Earned</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-primary-600 mb-1">
                          {earnedBadges.length}
                        </div>
                        <div className="text-sm text-gray-500">Badges Earned</div>
                      </div>
                    </div>

                    {earnedBadges.length > 0 && (
                      <div className="flex justify-center gap-4 mb-6">
                        {earnedBadges.map((badge) => (
                          <div key={badge} className="text-center">
                            <div className="p-3 bg-primary-100 rounded-full mb-2">
                              <Award className="h-6 w-6 text-primary-600" />
                            </div>
                            <div className="text-sm font-medium text-gray-700">
                              Problem Solver
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="outline"
                      onClick={onClose}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={() => {
                        setLives(3);
                        setScore(0);
                        setTimeLeft(challenge.duration * 60);
                        setCurrentTask(0);
                        setGameOver(false);
                        setEarnedBadges([]);
                      }}
                      icon={<ArrowRight className="h-4 w-4" />}
                      iconPosition="right"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ChallengeExperience;